/**
 * Category picking for bar and cashflow charts, by click and by keyboard on top
 * of the Recharts accessibility layer.
 */
import React from 'react';
import type { BarRectangleItem, MouseHandlerDataParam } from 'recharts';
import { ActiveLabelReporter } from './FmChartActiveLabel';

type BarClick = (
  item: BarRectangleItem,
  index: number,
  event: React.MouseEvent<SVGPathElement, MouseEvent>,
) => void;

/**
 * Category picking by click and by keyboard.
 *
 * A bar click reads the category from the clicked bar itself, so a tap with
 * no preceding hover still picks it; a click elsewhere in a column uses the
 * Recharts active category under the pointer. Arrow keys move the active
 * category (accessibility layer); Enter or Space selects it.
 */
export function useChartCategorySelection(
  onSelect: ((category: string) => void) | undefined,
  categoryKey: string,
): {
  reporter: React.ReactElement | null;
  onClick: ((state: MouseHandlerDataParam) => void) | undefined;
  onBarClick: BarClick | undefined;
  onKeyDown: ((event: KeyboardEvent) => void) | undefined;
} {
  const activeRef = React.useRef<string | undefined>(undefined);
  const track = React.useCallback((label: string | undefined) => {
    activeRef.current = label;
  }, []);
  if (!onSelect) {
    return { reporter: null, onClick: undefined, onBarClick: undefined, onKeyDown: undefined };
  }
  return {
    reporter: <ActiveLabelReporter onChange={track} />,
    onClick: (state) => {
      if (state.activeLabel !== undefined && state.activeLabel !== null) {
        onSelect(String(state.activeLabel));
      }
    },
    onBarClick: (item, _index, event) => {
      const category: unknown = item.payload?.[categoryKey];
      if (category === undefined || category === null) return;
      // The bar names its own category; the column handler would read the
      // tooltip state, which on a first tap still points at the focus default.
      event.stopPropagation();
      onSelect(String(category));
    },
    onKeyDown: (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (activeRef.current === undefined) return;
      event.preventDefault();
      onSelect(activeRef.current);
    },
  };
}
